import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Loader2, CreditCard, MessageCircle, Gift, Info, MapPin, Plus, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { SpiceNote } from "./SpiceNote";
import { api, errMsg } from "../lib/api";

const EMPTY_ADDR = { line1: "", line2: "", city: "", state: "", pincode: "", notes: "", label: "" };

/**
 * 2-step checkout: (1) address (with reuse picker for existing users), (2) payment.
 */
export const CheckoutModal = ({ open, onClose }) => {
    const { user, config, refresh, setAuthOpen, setAuthMode } = useAuth();
    const { items, subtotal, weightGrams, clear, setOpen: setCartOpen } = useCart();
    const { lang } = useLang();

    const [step, setStep] = useState(1);
    const [addr, setAddr] = useState(EMPTY_ADDR);
    const [guest, setGuest] = useState({ name: "", phone: "" });
    const [saveNew, setSaveNew] = useState(true); // save this new address to profile
    const [mode, setMode] = useState("pick"); // 'pick' | 'new'   (only relevant when user has saved addrs)
    const [selectedId, setSelectedId] = useState("");
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState("");
    const [done, setDone] = useState(null);

    const savedAddresses = user?.addresses || [];
    const hasSaved = savedAddresses.length > 0;

    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const has250g = items.some((i) => i.weight === "250g");
    const blocked250g = has250g && !!quote && !quote.allows_250g;
    const shipping = quote?.shipping ?? 0;
    const total = subtotal + shipping;

    const t = (en, te) => (lang === "en" ? en : te);

    // Whenever the modal opens (or the user's saved-address list changes while open),
    // reset state and pick the first saved address by default.
    useEffect(() => {
        if (open) {
            setError("");
            setDone(null);
            setStep(1);
            setQuote(null);
            if (hasSaved) {
                setMode("pick");
                setSelectedId(savedAddresses[0].id);
                setAddr(EMPTY_ADDR);
            } else {
                setMode("new");
                setSelectedId("");
                setAddr(EMPTY_ADDR);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, hasSaved, savedAddresses.length]);

    const close = () => {
        onClose();
        setTimeout(() => {
            setStep(1);
            setError("");
            setDone(null);
        }, 400);
    };

    const validateNewAddr = () => {
        if (!addr.line1.trim() || !addr.city.trim() || !addr.state.trim() || !/^\d{6}$/.test(addr.pincode)) {
            setError(t("Please fill address, city, state and a valid 6-digit pincode.", "చిరునామా, నగరం, రాష్ట్రం, 6 అంకెల పిన్‌కోడ్ నమోదు చేయండి."));
            return false;
        }
        return true;
    };

    const goToPayment = (e) => {
        e.preventDefault();
        setError("");
        if (!user) {
            if (guest.name.trim().length < 2) {
                setError(t("Please enter your name.", "మీ పేరు నమోదు చేయండి."));
                return;
            }
            if (!/^[6-9]\d{9}$/.test(guest.phone)) {
                setError(t("Please enter a valid 10-digit mobile number.", "సరైన 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి."));
                return;
            }
        }
        if (mode === "pick") {
            if (!selectedId) {
                setError(t("Please select a delivery address.", "డెలివరీ చిరునామాను ఎంచుకోండి."));
                return;
            }
        } else {
            if (!validateNewAddr()) return;
        }
        setQuote(null);
        setStep(2);
        fetchQuote();
    };

    // Shipping charges are revealed only once we know the destination pincode.
    const fetchQuote = async () => {
        const pincode = getEffectiveAddress().pincode;
        setQuoteLoading(true);
        try {
            const { data } = await api.get(`/shipping/estimate?pincode=${pincode}&subtotal=${subtotal}&weight_grams=${weightGrams}`);
            setQuote(data);
        } catch (err) {
            setError(errMsg(err, t("Could not fetch shipping for this pincode.", "ఈ పిన్‌కోడ్‌కు షిప్పింగ్ పొందలేకపోయాము.")));
        } finally {
            setQuoteLoading(false);
        }
    };

    const getEffectiveAddress = () => {
        if (mode === "pick") {
            const found = savedAddresses.find((a) => a.id === selectedId);
            if (found) {
                // Backend Address model rejects extra fields silently; send only its fields
                const { line1, line2, city, state, pincode, notes } = found;
                return { line1, line2: line2 || "", city, state, pincode, notes: notes || "" };
            }
        }
        const { line1, line2, city, state, pincode, notes } = addr;
        return { line1, line2: line2 || "", city, state, pincode, notes: notes || "" };
    };

    const buildItems = () => items.map((i) => ({
        id: i.id, name_en: i.name.en, name_te: i.name.te, price: i.price, weight: i.weight, qty: i.qty,
    }));

    const buildPayload = (payment_method) => ({
        items: buildItems(),
        address: getEffectiveAddress(),
        payment_method,
        save_address: !!user && mode === "new" && saveNew,
        address_label: mode === "new" ? (addr.label || "") : "",
        ...(user ? {} : { guest_name: guest.name.trim(), guest_phone: guest.phone }),
    });

    const afterOrder = async () => {
        try { await refresh(); } catch { /* ignore */ }
    };

    const submitWhatsApp = async () => {
        setLoading("whatsapp"); setError("");
        try {
            const { data } = await api.post("/orders", buildPayload("whatsapp"));
            setDone({ whatsapp_url: data.whatsapp_url, method: "whatsapp" });
            clear(); setCartOpen(false);
            await afterOrder();
            window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
        } catch (e) { setError(errMsg(e)); }
        finally { setLoading(null); }
    };

    const submitRazorpay = async () => {
        setLoading("razorpay"); setError("");
        try {
            const { data } = await api.post("/orders", buildPayload("razorpay"));
            const rzp = window.Razorpay;
            if (!rzp) {
                setError(t("Payment SDK failed to load. Please retry.", "పేమెంట్ లోడ్ కాలేదు."));
                return;
            }
            const options = {
                key: data.razorpay_key_id,
                amount: data.amount_paise,
                currency: data.currency,
                name: "Bheema's Kumari Pickles",
                description: `Order · ${items.length} items`,
                order_id: data.razorpay_order_id,
                prefill: {
                    name: user?.name || guest.name,
                    contact: user?.phone || guest.phone,
                    email: user?.email || "",
                },
                theme: { color: "#C8553D" },
                handler: async (resp) => {
                    try {
                        const verify = await api.post("/orders/verify", {
                            order_db_id: data.order_db_id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        setDone({ whatsapp_url: verify.data.whatsapp_url, method: "razorpay" });
                        clear(); setCartOpen(false);
                        await afterOrder();
                        if (verify.data.whatsapp_url) window.open(verify.data.whatsapp_url, "_blank");
                    } catch (e) { setError(errMsg(e, t("Verification failed", "ధృవీకరణ విఫలం"))); }
                },
                modal: { ondismiss: () => setLoading(null) },
            };
            new rzp(options).open();
        } catch (e) { setError(errMsg(e)); }
        finally {
            if (loading === "razorpay") setLoading(null);
        }
    };

    if (!open) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-[color:var(--kp-ink)]/60 backdrop-blur-md"
                onClick={close} data-testid="checkout-modal"
            >
                <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.98 }}
                    transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-[540px] max-h-[92vh] overflow-y-auto bg-[color:var(--kp-cream)] rounded-3xl p-6 md:p-9"
                >
                    <button onClick={close} className="absolute top-4 right-4 p-1" aria-label="Close" data-testid="checkout-close">
                        <X size={18} />
                    </button>

                    {done ? (
                        <div className="py-6" data-testid="checkout-success">
                            <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-terracotta)] mb-2">
                                {done.method === "razorpay"
                                    ? t("PAYMENT RECEIVED", "చెల్లింపు పూర్తయింది")
                                    : t("ORDER SENT", "ఆర్డర్ పంపబడింది")}
                            </div>
                            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-editorial mb-4">
                                {t("Thank you.", "ధన్యవాదాలు.")}{" "}
                                <em className="italic text-[color:var(--kp-terracotta)] font-medium">
                                    {t("Kitchen is warming up.", "వంటగది సిద్ధమవుతోంది.")}
                                </em>
                            </h2>
                            <p className="text-[color:var(--kp-ink-soft)] mb-6">
                                {done.method === "razorpay"
                                    ? t("Payment confirmed. We've opened WhatsApp so you can share delivery preferences directly with Kumari.", "చెల్లింపు పూర్తయింది. WhatsApp లో వివరాలు పంపండి.")
                                    : t("Your order details are pre-filled in WhatsApp — hit send to notify the kitchen.", "మీ ఆర్డర్ WhatsApp లో సిద్ధంగా ఉంది — సెండ్ చేయండి.")}
                            </p>
                            {done.whatsapp_url && (
                                <a
                                    href={done.whatsapp_url} target="_blank" rel="noopener noreferrer"
                                    className="btn-warm w-full justify-center"
                                    data-testid="checkout-open-whatsapp"
                                >
                                    {t("Message the kitchen on WhatsApp", "వంటగదికి WhatsApp")}
                                </a>
                            )}
                            <button onClick={close} className="w-full mt-3 btn-outline-warm justify-center" data-testid="checkout-close-done">
                                {t("Close", "మూసివేయి")}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-terracotta)] mb-2">
                                {t("STEP " + step + " / 2 · CHECKOUT", "దశ " + step + " / 2 · చెక్అవుట్")}
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl leading-[0.98] tracking-editorial mb-6 font-bold">
                                {step === 1
                                    ? <>{t("Where do we", "ఎక్కడికి")} <em className="italic text-[color:var(--kp-terracotta)]">{t("send it?", "పంపాలి?")}</em></>
                                    : <>{t("How would you", "ఎలా")} <em className="italic text-[color:var(--kp-terracotta)]">{t("like to pay?", "చెల్లిస్తారు?")}</em></>}
                            </h2>

                            {error && (
                                <div className="mb-4 text-sm text-[color:var(--kp-terracotta)] bg-[color:var(--kp-terracotta)]/8 border border-[color:var(--kp-terracotta)]/25 px-3 py-2 rounded-xl" data-testid="checkout-error">
                                    {error}
                                </div>
                            )}

                            {step === 1 && (
                                <div className="space-y-3" data-testid="checkout-step-address">
                                    {!user && (
                                        <>
                                            <div
                                                className="rounded-2xl border-2 border-dashed border-[color:var(--kp-turmeric)] bg-[color:var(--kp-turmeric)]/12 px-4 py-3.5 mb-1"
                                                data-testid="guest-login-nudge"
                                            >
                                                <div className="flex items-start gap-2.5">
                                                    <Gift size={16} className="mt-0.5 shrink-0 text-[color:var(--kp-terracotta)]" />
                                                    <div className="min-w-0">
                                                        <div className="text-[12px] font-bold text-[color:var(--kp-ink)] leading-snug">
                                                            {t(
                                                                "Checking out as a guest is perfectly fine.",
                                                                "గెస్ట్‌గా ఆర్డర్ చేయవచ్చు — ఇబ్బంది లేదు.",
                                                            )}
                                                        </div>
                                                        <p className="text-[11px] text-[color:var(--kp-ink-soft)] leading-snug mt-1">
                                                            {t(
                                                                "But members get first dibs on festival batches, quiet discount codes on WhatsApp, saved addresses and one-tap reorders. It takes 20 seconds — and Kumari Amma remembers her regulars.",
                                                                "కానీ సభ్యులకు పండుగ బ్యాచ్‌లు ముందుగా, WhatsApp లో ప్రత్యేక తగ్గింపులు, సేవ్ చేసిన చిరునామాలు, ఒక్క టాప్‌లో మళ్ళీ ఆర్డర్. 20 సెకన్లు చాలు.",
                                                            )}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-2.5">
                                                            <button
                                                                onClick={() => { setAuthMode("signup"); setAuthOpen(true); }}
                                                                className="px-3 py-1.5 rounded-full bg-[color:var(--kp-terracotta)] text-[color:var(--kp-cream)] text-[10px] tracking-mono-wide font-bold"
                                                                data-testid="guest-create-account"
                                                            >
                                                                {t("CREATE ACCOUNT", "ఖాతా సృష్టించండి")}
                                                            </button>
                                                            <button
                                                                onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
                                                                className="px-3 py-1.5 rounded-full border-2 border-[color:var(--kp-terracotta)]/40 text-[color:var(--kp-terracotta)] text-[10px] tracking-mono-wide font-bold"
                                                                data-testid="guest-login"
                                                            >
                                                                {t("LOG IN", "లాగిన్")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <FieldRow
                                                    label={t("Your name", "మీ పేరు")}
                                                    value={guest.name}
                                                    onChange={(v) => setGuest({ ...guest, name: v })}
                                                    testId="input-guest-name"
                                                />
                                                <FieldRow
                                                    label={t("Mobile number", "మొబైల్ నంబర్")}
                                                    inputMode="numeric"
                                                    value={guest.phone}
                                                    onChange={(v) => setGuest({ ...guest, phone: v.replace(/\D/g, "").slice(0, 10) })}
                                                    testId="input-guest-phone"
                                                />
                                            </div>
                                        </>
                                    )}
                                    {hasSaved && (
                                        <div className="mb-2 flex items-center gap-2 text-[11px] tracking-mono-wide text-[color:var(--kp-ink-soft)]">
                                            <button
                                                onClick={() => { setMode("pick"); setError(""); }}
                                                className={`px-3 py-1.5 rounded-full border-2 transition-colors ${
                                                    mode === "pick"
                                                        ? "border-[color:var(--kp-terracotta)] text-[color:var(--kp-terracotta)] bg-[color:var(--kp-terracotta)]/5"
                                                        : "border-[color:var(--kp-ink)]/15 text-[color:var(--kp-ink-soft)]"
                                                }`}
                                                data-testid="addr-mode-pick"
                                            >
                                                {t("USE SAVED", "సేవ్ చేసినది")}
                                            </button>
                                            <button
                                                onClick={() => { setMode("new"); setSelectedId(""); setError(""); }}
                                                className={`px-3 py-1.5 rounded-full border-2 transition-colors flex items-center gap-1 ${
                                                    mode === "new"
                                                        ? "border-[color:var(--kp-terracotta)] text-[color:var(--kp-terracotta)] bg-[color:var(--kp-terracotta)]/5"
                                                        : "border-[color:var(--kp-ink)]/15 text-[color:var(--kp-ink-soft)]"
                                                }`}
                                                data-testid="addr-mode-new"
                                            >
                                                <Plus size={11} /> {t("ADD NEW", "కొత్తది")}
                                            </button>
                                        </div>
                                    )}

                                    {mode === "pick" && hasSaved && (
                                        <div className="space-y-2" data-testid="addr-picker">
                                            {savedAddresses.map((a) => {
                                                const selected = a.id === selectedId;
                                                return (
                                                    <button
                                                        key={a.id}
                                                        onClick={() => setSelectedId(a.id)}
                                                        className={`w-full text-left p-4 rounded-2xl border-2 transition-colors ${
                                                            selected
                                                                ? "border-[color:var(--kp-terracotta)] bg-[color:var(--kp-terracotta)]/5"
                                                                : "border-[color:var(--kp-ink)]/10 bg-[color:var(--kp-paper)] hover:border-[color:var(--kp-ink)]/25"
                                                        }`}
                                                        data-testid={`addr-option-${a.id}`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className={`w-4 h-4 rounded-full mt-1 shrink-0 flex items-center justify-center border-2 ${
                                                                selected
                                                                    ? "border-[color:var(--kp-terracotta)] bg-[color:var(--kp-terracotta)]"
                                                                    : "border-[color:var(--kp-ink)]/25"
                                                            }`}>
                                                                {selected && <Check size={10} className="text-[color:var(--kp-cream)]" />}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                {a.label && (
                                                                    <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-terracotta)] font-bold mb-1">
                                                                        {a.label.toUpperCase()}
                                                                    </div>
                                                                )}
                                                                <div className="text-sm text-[color:var(--kp-ink)] leading-snug">
                                                                    {a.line1}{a.line2 ? `, ${a.line2}` : ""}
                                                                </div>
                                                                <div className="text-xs text-[color:var(--kp-ink-soft)] mt-0.5">
                                                                    {a.city}, {a.state} — {a.pincode}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {mode === "new" && (
                                        <form onSubmit={goToPayment} className="space-y-3" data-testid="checkout-form-address">
                                            {hasSaved && (
                                                <FieldRow label={t("Label (Home / Office · optional)", "లేబుల్ (ఇల్లు / ఆఫీస్ · ఐచ్ఛికం)")} value={addr.label} onChange={(v) => setAddr({ ...addr, label: v })} testId="input-label" />
                                            )}
                                            <FieldRow label={t("Address line 1", "చిరునామా 1")} value={addr.line1} onChange={(v) => setAddr({ ...addr, line1: v })} testId="input-line1" />
                                            <FieldRow label={t("Address line 2 (optional)", "చిరునామా 2 (ఐచ్ఛికం)")} value={addr.line2} onChange={(v) => setAddr({ ...addr, line2: v })} testId="input-line2" />
                                            <div className="grid grid-cols-2 gap-3">
                                                <FieldRow label={t("City", "నగరం")} value={addr.city} onChange={(v) => setAddr({ ...addr, city: v })} testId="input-city" />
                                                <FieldRow label={t("State", "రాష్ట్రం")} value={addr.state} onChange={(v) => setAddr({ ...addr, state: v })} testId="input-state" />
                                            </div>
                                            <FieldRow label={t("Pincode", "పిన్‌కోడ్")} inputMode="numeric" value={addr.pincode} onChange={(v) => setAddr({ ...addr, pincode: v.replace(/\D/g, "").slice(0, 6) })} testId="input-pincode" />
                                            <FieldRow label={t("Delivery notes (optional)", "డెలివరీ నోట్స్")} value={addr.notes} onChange={(v) => setAddr({ ...addr, notes: v })} testId="input-notes" />
                                            <label className="flex items-center gap-2 text-[11px] tracking-mono-wide text-[color:var(--kp-ink-soft)] cursor-pointer pt-1">
                                                <input
                                                    type="checkbox"
                                                    checked={saveNew}
                                                    onChange={(e) => setSaveNew(e.target.checked)}
                                                    className="accent-[color:var(--kp-terracotta)]"
                                                    data-testid="checkbox-save-address"
                                                />
                                                {t("Save this address for future orders", "తర్వాత ఆర్డర్ల కోసం సేవ్ చేయి")}
                                            </label>
                                        </form>
                                    )}

                                    <button
                                        onClick={goToPayment}
                                        className="btn-warm w-full justify-between mt-4"
                                        data-testid="checkout-to-payment"
                                    >
                                        <span>{t("Continue to payment", "చెల్లింపుకు వెళ్ళండి")}</span>
                                        <ArrowRight size={14} />
                                    </button>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="space-y-4" data-testid="checkout-payment-methods">
                                    <div className="bg-[color:var(--kp-paper)] rounded-2xl p-4 border border-[color:var(--kp-ink)]/8">
                                        <div className="flex items-start gap-2 pb-3 mb-3 border-b border-[color:var(--kp-ink)]/10">
                                            <MapPin size={14} className="mt-0.5 text-[color:var(--kp-terracotta)]" />
                                            <div className="text-[11px] text-[color:var(--kp-ink-soft)] leading-snug flex-1">
                                                {getEffectiveAddress().line1}, {getEffectiveAddress().city} — {getEffectiveAddress().pincode}
                                            </div>
                                            <button
                                                onClick={() => setStep(1)}
                                                className="text-[10px] tracking-mono-wide text-[color:var(--kp-terracotta)] font-bold"
                                                data-testid="checkout-edit-addr"
                                            >
                                                {t("EDIT", "మార్చు")}
                                            </button>
                                        </div>
                                        <Row k="Subtotal" v={`₹${subtotal}`} />
                                        <Row
                                            k={
                                                <>
                                                    Shipping{" "}
                                                    {quote?.is_free && (
                                                        <em className="not-italic text-[color:var(--kp-banana)]">· Free</em>
                                                    )}
                                                </>
                                            }
                                            v={
                                                quoteLoading
                                                    ? t("Calculating…", "లెక్కిస్తున్నాము…")
                                                    : quote
                                                      ? `₹${shipping}`
                                                      : "—"
                                            }
                                        />
                                        {quote && (
                                            <div className="text-[10px] text-[color:var(--kp-ink-soft)] -mt-1" data-testid="checkout-eta">
                                                {quote.zone} · {t("Est. delivery", "అంచనా డెలివరీ")} {quote.eta_from} – {quote.eta_to}
                                            </div>
                                        )}
                                        <div className="flex justify-between items-baseline pt-2 border-t border-[color:var(--kp-ink)]/10">
                                            <span className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink-soft)]">TOTAL</span>
                                            <span className="font-display text-2xl font-bold" data-testid="checkout-total">₹{total}</span>
                                        </div>
                                        <p className="text-[10px] leading-snug text-[color:var(--kp-ink-soft)] pt-1" data-testid="checkout-free-shipping-note">
                                            {t(
                                                "Delivery charges are on us for orders of 1 kg or more — applicable only for Andhra Pradesh and Telangana states.",
                                                "1 కేజీ లేదా అంతకంటే ఎక్కువ ఆర్డర్లకు డెలివరీ ఛార్జీలు మా భారం — ఆంధ్రప్రదేశ్, తెలంగాణ రాష్ట్రాలకు మాత్రమే.",
                                            )}
                                        </p>
                                        {has250g && quote && !quote.allows_250g && (
                                            <p className="text-[11px] leading-snug text-[color:var(--kp-red)] font-bold pt-1" data-testid="checkout-250g-blocked">
                                                {t(
                                                    "250 g packs are sold only to Andhra Pradesh and Telangana pincodes. Please change those items to 500 g or 1 kg to continue.",
                                                    "250 గ్రా. ప్యాకెట్లు ఆంధ్రప్రదేశ్, తెలంగాణ పిన్‌కోడ్‌లకు మాత్రమే. కొనసాగించడానికి 500 గ్రా. లేదా 1 కేజీకి మార్చండి.",
                                                )}
                                            </p>
                                        )}

                                    </div>

                                    {/* Razorpay */}
                                    <button
                                        onClick={submitRazorpay}
                                        disabled={!!loading || !config.razorpay_ready || !quote || blocked250g}
                                        className={`w-full flex items-center justify-between rounded-full px-5 py-4 text-[12px] tracking-mono-wide font-bold transition-colors ${
                                            config.razorpay_ready
                                                ? "bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] hover:bg-black"
                                                : "bg-[color:var(--kp-cream-2)] text-[color:var(--kp-ink)]/40 cursor-not-allowed"
                                        }`}
                                        data-testid="pay-razorpay"
                                    >
                                        <span className="flex items-center gap-2">
                                            {loading === "razorpay" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                                            {t("Pay Online · Card / UPI / NetBanking", "ఆన్‌లైన్ చెల్లింపు · UPI / కార్డ్")}
                                        </span>
                                        <span>₹{total}</span>
                                    </button>
                                    {!config.razorpay_ready && (
                                        <div className="flex items-start gap-2 text-[11px] text-[color:var(--kp-ink-soft)] -mt-2 px-2" data-testid="razorpay-unavailable-note">
                                            <Info size={12} className="mt-0.5 shrink-0" />
                                            <span>{t("Online payments are being set up. Please place your order on WhatsApp for now — we'll send you a secure payment link.", "ఆన్‌లైన్ చెల్లింపు త్వరలో. ఇప్పటికి WhatsApp లో ఆర్డర్ చేయండి — మేము పేమెంట్ లింక్ పంపుతాము.")}</span>
                                        </div>
                                    )}

                                    {/* WhatsApp */}
                                    <button
                                        onClick={submitWhatsApp}
                                        disabled={!!loading || !quote || blocked250g}
                                        className="w-full flex items-center justify-between rounded-full px-5 py-4 text-[12px] tracking-mono-wide font-bold bg-transparent text-[color:var(--kp-terracotta)] border-2 border-[color:var(--kp-terracotta)]/40 hover:bg-[color:var(--kp-terracotta)]/5 disabled:opacity-60"
                                        data-testid="pay-whatsapp"
                                    >
                                        <span className="flex items-center gap-2">
                                            {loading === "whatsapp" ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
                                            {t("Order via WhatsApp · Confirm & pay later", "వాట్సాప్‌లో ఆర్డర్ · తర్వాత చెల్లింపు")}
                                        </span>
                                        <ArrowRight size={14} />
                                    </button>

                                    <button
                                        onClick={() => setStep(1)}
                                        className="text-[12px] tracking-mono-wide text-[color:var(--kp-ink-soft)] hover:text-[color:var(--kp-ink)] inline-flex items-center gap-2 mt-2"
                                        data-testid="checkout-back"
                                    >
                                        <ArrowLeft size={12} />
                                        {t("Back to address", "చిరునామాకు")}
                                    </button>

                                    <SpiceNote testId="checkout-spice-custom-note" className="mt-2" />
                                </div>
                            )}
                        </>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

const Row = ({ k, v }) => (
    <div className="flex justify-between text-sm py-1">
        <span className="text-[color:var(--kp-ink-soft)]">{k}</span>
        <span>{v}</span>
    </div>
);

const FieldRow = ({ label, value, onChange, testId, inputMode }) => (
    <label className="block">
        <span className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink-soft)] mb-1 block">{label}</span>
        <input
            value={value}
            inputMode={inputMode}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="w-full bg-[color:var(--kp-paper)] py-3 px-4 border border-[color:var(--kp-ink)]/15 focus:border-[color:var(--kp-terracotta)] rounded-full outline-none text-[color:var(--kp-ink)] transition-colors"
        />
    </label>
);
